/**
 * Mixpanel SDK wrapper service - single point of control for all analytics
 */

import mixpanel from 'mixpanel-browser'

class MixpanelService {
    constructor() {
        if (MixpanelService.instance) {
            return MixpanelService.instance
        }
        MixpanelService.instance = this

        this._initialized = false
        this._debug = false
    }

    /**
     * Initialize Mixpanel SDK - must be called before Vue mount
     */
    init(token, config = {}) {
        if (this._initialized) {
            console.warn('MixpanelService: Already initialized')
            return
        }

        if (!token) {
            console.warn('MixpanelService: No token provided, tracking disabled')
            return
        }

        const defaultConfig = {
            persistence: 'localStorage',
            track_pageview: false,
            batch_requests: true,
            api_host: 'https://api-js.mixpanel.com'
        }

        const mergedConfig = { ...defaultConfig, ...config }
        this._debug = mergedConfig.debug || false

        try {
            mixpanel.init(token, mergedConfig)
            this._initialized = true
            this._log('Initialized with config', mergedConfig)
        } catch (error) {
            console.error('MixpanelService: Failed to initialize', error)
        }
    }

    /**
     * Track an event with properties
     */
    track(event, props = {}) {
        if (!this._initialized) {
            this._log('Not initialized, event not tracked:', event)
            return
        }

        if (!event || typeof event !== 'string') {
            console.error('MixpanelService: Invalid event name:', event)
            return
        }

        this._safeCall(() => {
            mixpanel.track(event, props)
            this._log('Tracked', event, props)
        }, 'track event')
    }

    /**
     * Register super properties (attached to all events)
     */
    registerSuperProps(props) {
        if (!this._initialized) {
            this._log('Not initialized, super props not registered')
            return
        }

        this._safeCall(() => {
            mixpanel.register(props)
            this._log('Registered super props', props)
        }, 'register super props')
    }

    /**
     * Unregister a super property
     */
    unregisterSuperProp(key) {
        if (!this._initialized) return

        this._safeCall(() => {
            mixpanel.unregister(key)
            this._log('Unregistered super prop', key)
        }, 'unregister super prop')
    }

    /**
     * Identify a user - call after login, before tracking completion events
     */
    identify(userId) {
        if (!this._initialized) return

        if (!userId || typeof userId !== 'string') {
            console.error('MixpanelService: Invalid userId for identify')
            return
        }

        this._safeCall(() => {
            mixpanel.identify(userId)
            this._log('Identified user', userId)
        }, 'identify user')
    }

    /**
     * Reset identity - call on logout to start fresh anonymous session
     */
    reset() {
        if (!this._initialized) return

        this._safeCall(() => {
            mixpanel.reset()
            this._log('Reset identity')
        }, 'reset')
    }

    /**
     * Set user profile properties
     */
    setProfileProps(props) {
        if (!this._initialized) return

        this._safeCall(() => {
            mixpanel.people.set(props)
            this._log('Set profile props', props)
        }, 'set profile props')
    }

    /**
     * Opt out of tracking (for privacy compliance)
     */
    optOut() {
        if (!this._initialized) return

        this._safeCall(() => {
            mixpanel.opt_out_tracking()
            this._log('Opted out of tracking')
        }, 'opt out')
    }

    /**
     * Opt back into tracking
     */
    optIn() {
        if (!this._initialized) return

        this._safeCall(() => {
            mixpanel.opt_in_tracking()
            this._log('Opted in to tracking')
        }, 'opt in')
    }

    /**
     * Check if user has opted out
     */
    hasOptedOut() {
        if (!this._initialized) return false

        try {
            return mixpanel.has_opted_out_tracking()
        } catch (error) {
            console.error('MixpanelService: Failed to check opt out status', error)
            return false
        }
    }

    /**
     * Get the current distinct ID
     */
    getDistinctId() {
        if (!this._initialized) return null

        try {
            return mixpanel.get_distinct_id()
        } catch (error) {
            console.error('MixpanelService: Failed to get distinct ID', error)
            return null
        }
    }

    isInitialized() {
        return this._initialized
    }

    // Helper to wrap SDK calls with error handling
    _safeCall(fn, operation) {
        try {
            fn()
        } catch (error) {
            console.error(`MixpanelService: Failed to ${operation}`, error)
        }
    }

    // Helper for debug logging
    _log(...args) {
        if (this._debug) {
            console.log('MixpanelService:', ...args)
        }
    }
}

export default new MixpanelService()
